import { FC, Fragment, useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  agencyDeliveryDashboardDataAtom,
  agencyDeliveryDashboardStatusAtom,
  agencyDeliveryStatisticsDataAtom,
  giftDeliverySearchDataAtomFamily,
  giftDeliverySearchStatusAtomFamily,
} from '@shared/api/giftDelivery/giftDelivery.atom';
import {
  UpdateDeliveryStatusDto,
  updateGiftDeliveryRecipient,
  updateGiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  agencyDeliveryDashboardUrl,
  agencyDeliveryStatisticsUrl,
  giftDeliveryDeliveredKey,
  giftDeliveryDeliveredUrl,
  useAgencyDeliveryDashboardHook,
  useAgencyDeliveryStatisticsHook,
  useGiftDeliveryDeliveredHook,
} from '@shared/api/giftDelivery/giftDelivery.hook';
import {
  GiftDeliveryForAdmin,
  GiftDeliveryStatus,
  GiftDeliveryType,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { postImage } from '@shared/api/uploadImage';
import { Loading } from '@shared/components/Loading';
import StatPageSideBar from '@shared/components/StatPageSideBar';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { recipientRegionPatch } from '@shared/utils/giftAddressRegion';
import { useQueryClient } from 'react-query';

import { useTitleHook } from '../../bugo/hooks/useTitleHook';
import { useToast } from '../hooks/useToast';
import { DeliveredDeliveryTable } from '../components/DeliveredDeliveryTable';
import { Toast } from '../components/Toast';
import { DeliveredEditModal } from '../components/DeliveredEditModal';
import { DesiredDeliveryDateBadge } from '../components/DesiredDeliveryDateBadge';
import { ImagePreviewModal } from '../components/ImagePreviewModal';
import { RecipientEditModal } from '../components/RecipientEditModal';
import { StatusChangeModal } from '../components/StatusChangeModal';
import {
  nextStatusLabel,
  nextStatusMap,
  prevStatusLabel,
  prevStatusMap,
  statusLabel,
  typeBadgeStyle,
  typeLabel,
} from '../utils/delivery.constants';
import {
  EditModalState,
  ImageModalState,
  RecipientModalState,
  StatusModalState,
} from '../utils/delivery.types';
import {
  formatDate,
  formatPhone,
  formatRelativeDateTime,
  formatShortDateTime,
  getImageUrl,
  getItemName,
  getOrdererInfo,
  toLocalDateTimeString,
} from '../utils/delivery.utils';

const ROLE = LowerRole.BugoAgencyAdmin;

// 필터 칩으로 노출되는 전체 상태 (배송완료 포함)
const FILTERABLE_STATUSES = [
  GiftDeliveryStatus.WaitingAddress,
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
  GiftDeliveryStatus.Delivered,
];

// 기본 선택: 진행중 상태 전체 (배송완료는 누적 이력이라 기본 제외, 칩으로 선택 가능)
const DEFAULT_STATUSES: GiftDeliveryStatus[] = [
  GiftDeliveryStatus.WaitingAddress,
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
];

// 칩에 건수를 노출할 "처리 대기/진행중" 상태 (완료는 누적이라 제외)
const COUNTABLE_STATUSES: GiftDeliveryStatus[] = [
  GiftDeliveryStatus.WaitingAddress,
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
];

// 발주를 기다리는 "신규 주문" 상태 (배송지 입력이 끝나 바로 발주 가능한 건)
const NEW_ORDER_STATUS = GiftDeliveryStatus.AddressSubmitted;

const AgencyAdminGiftDeliveryContent: FC = function AgencyAdminGiftDeliveryContent() {
  useTitleHook('배송상품 관리');
  useAgencyDeliveryDashboardHook();
  useAgencyDeliveryStatisticsHook();

  const queryClient = useQueryClient();
  const dashboardStatus = useRecoilValue(agencyDeliveryDashboardStatusAtom);
  const dashboard = useRecoilValue(agencyDeliveryDashboardDataAtom);
  const statistics = useRecoilValue(agencyDeliveryStatisticsDataAtom);

  const invalidateDashboard = useCallback(async () => {
    await queryClient.invalidateQueries(agencyDeliveryDashboardUrl);
    await queryClient.invalidateQueries(agencyDeliveryStatisticsUrl);
    await queryClient.invalidateQueries(giftDeliveryDeliveredUrl(ROLE));
  }, [queryClient]);

  const todayDelivered = useMemo(() => dashboard?.todayDelivered ?? [], [dashboard]);

  // 여러 상태를 동시에 선택 가능 (다중 선택 칩)
  const [statusFilter, setStatusFilter] =
    useState<GiftDeliveryStatus[]>(DEFAULT_STATUSES);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [showTodayProcessed, setShowTodayProcessed] = useState(false);
  const [copiedAddressId, setCopiedAddressId] = useState<string | null>(null);

  const {
    message: toastMessage,
    show: showToast,
    durationMs: toastDurationMs,
  } = useToast(1200);

  const { assetRole } = useAuth();

  // 배송완료 칩 선택 시에만 전체 배송완료 내역을 조회한다 (그 외엔 skip)
  const showDelivered = statusFilter.includes(GiftDeliveryStatus.Delivered);
  useGiftDeliveryDeliveredHook(ROLE, !showDelivered);
  const deliveredAll = useRecoilValue(
    giftDeliverySearchDataAtomFamily(giftDeliveryDeliveredKey(ROLE)),
  );
  const deliveredStatus = useRecoilValue(
    giftDeliverySearchStatusAtomFamily(giftDeliveryDeliveredKey(ROLE)),
  );
  const deliveredLoading =
    showDelivered && (deliveredStatus === 'loading' || deliveredStatus === 'idle');

  const [statusModal, setStatusModal] = useState<StatusModalState | null>(null);
  const [recipientModal, setRecipientModal] = useState<RecipientModalState | null>(null);
  const [imageModal, setImageModal] = useState<ImageModalState | null>(null);
  const [editModal, setEditModal] = useState<EditModalState | null>(null);

  // 선택된 상태 칩들의 목록을 합쳐 검색/타입 필터 적용 (최신순)
  const rows = useMemo(() => {
    const byStatus: Partial<Record<GiftDeliveryStatus, GiftDeliveryForAdmin[]>> = {
      [GiftDeliveryStatus.WaitingAddress]: dashboard?.waitingAddress ?? [],
      [GiftDeliveryStatus.AddressSubmitted]: dashboard?.addressSubmitted ?? [],
      [GiftDeliveryStatus.Preparing]: dashboard?.preparing ?? [],
      [GiftDeliveryStatus.Shipping]: dashboard?.shipping ?? [],
      [GiftDeliveryStatus.Delivered]: deliveredAll,
    };
    let result = statusFilter.flatMap((s) => byStatus[s] ?? []);

    if (typeFilter !== 'all') {
      result = result.filter((d) => d.type === typeFilter);
    }

    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((d) => {
        const recipientPhone = d.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const recipientName = d.recipient?.name?.toLowerCase() || '';
        const itemName = getItemName(d).toLowerCase();
        const address = d.recipient?.address?.toLowerCase() || '';
        return (
          recipientPhone.includes(search) ||
          recipientName.includes(search) ||
          itemName.includes(search) ||
          address.includes(search)
        );
      });
    }

    return [...result].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [dashboard, deliveredAll, statusFilter, typeFilter, searchText]);

  // 상품옵션 열: 보이는 건 중 옵션을 고른 주문이 있을 때만 (옵션 없는 상품만 있으면 열을 숨긴다)
  const showOptionColumn = rows.some((d) => (d.selectedOptions?.length ?? 0) > 0);

  // 칩 뱃지용 상태별 건수 (통계 API)
  const statusCounts = useMemo<Partial<Record<GiftDeliveryStatus, number>>>(
    () => ({
      [GiftDeliveryStatus.WaitingAddress]: statistics?.waitingAddress,
      [GiftDeliveryStatus.AddressSubmitted]: statistics?.addressSubmitted,
      [GiftDeliveryStatus.Preparing]: statistics?.preparing,
      [GiftDeliveryStatus.Shipping]: statistics?.shipping,
    }),
    [statistics],
  );

  // 발주 대기 중인 신규 주문 건수 (배너/강조 트리거)
  const newOrderCount = statistics?.addressSubmitted ?? 0;

  const toggleStatus = (s: GiftDeliveryStatus) =>
    setStatusFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  const copyAddress = useCallback(async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAddressId(id);
      setTimeout(() => setCopiedAddressId(null), 1500);
    } catch {
      alert('복사에 실패했습니다.');
    }
  }, []);

  const handleRevertStatus = useCallback(
    async (deliveryId: string, currentStatus: GiftDeliveryStatus) => {
      const prev = prevStatusMap[currentStatus];
      if (!prev) return;
      const label = prevStatusLabel[currentStatus] || '이전 상태로 되돌리기';
      if (!confirm(`${label} 하시겠습니까?`)) return;
      setRevertingId(deliveryId);
      try {
        await updateGiftDeliveryStatus(deliveryId, prev, undefined, ROLE);
        await invalidateDashboard();
        alert('상태가 되돌려졌습니다.');
      } catch (error) {
        console.error('상태 되돌리기 실패:', error);
        alert('상태 되돌리기에 실패했습니다.');
      } finally {
        setRevertingId(null);
      }
    },
    [invalidateDashboard],
  );

  // 배송완료 건 수정 모달 (완료시각/완료사진, 배송중 원복 포함)
  const openEditModal = useCallback((delivery: GiftDeliveryForAdmin) => {
    const deliveredImg = delivery.shipping?.deliveredImage;
    const existingUrl =
      deliveredImg && typeof deliveredImg === 'object'
        ? fileBucketUrl(deliveredImg.url, 'w400')
        : null;
    setEditModal({
      deliveryId: delivery._id,
      itemName: getItemName(delivery),
      address: delivery.recipient?.address || '-',
      deliveredAt: delivery.shipping?.deliveredAt
        ? toLocalDateTimeString(new Date(delivery.shipping.deliveredAt))
        : toLocalDateTimeString(),
      imageFile: null,
      imagePreview: null,
      existingImageUrl: existingUrl,
      isSaving: false,
      showRevertConfirm: false,
      revertConfirmText: '',
    });
  }, []);

  const handleRevertDelivered = useCallback(async () => {
    if (!editModal) return;
    setEditModal((prev) => (prev ? { ...prev, isSaving: true } : null));
    try {
      await updateGiftDeliveryStatus(
        editModal.deliveryId,
        GiftDeliveryStatus.Shipping,
        undefined,
        ROLE,
      );
      await invalidateDashboard();
      setEditModal(null);
      alert('배송중 상태로 원복되었습니다.');
    } catch (error) {
      console.error('원복 실패:', error);
      alert('원복에 실패했습니다.');
      setEditModal((prev) => (prev ? { ...prev, isSaving: false } : null));
    }
  }, [editModal, invalidateDashboard]);

  const handleSaveEdit = useCallback(async () => {
    if (!editModal) return;
    setEditModal((prev) => (prev ? { ...prev, isSaving: true } : null));

    try {
      let deliveredImageId: string | undefined;
      if (editModal.imageFile) {
        const img = await postImage(editModal.imageFile, assetRole());
        deliveredImageId = img._id;
      }

      await updateGiftDeliveryStatus(
        editModal.deliveryId,
        GiftDeliveryStatus.Delivered,
        {
          deliveredAt: new Date(editModal.deliveredAt).toISOString(),
          ...(deliveredImageId ? { deliveredImage: deliveredImageId } : {}),
        },
        ROLE,
      );
      await invalidateDashboard();
      setEditModal(null);
      alert('수정이 완료되었습니다.');
    } catch (error) {
      console.error('수정 실패:', error);
      alert('수정에 실패했습니다.');
      setEditModal((prev) => (prev ? { ...prev, isSaving: false } : null));
    }
  }, [editModal, assetRole, invalidateDashboard]);

  // 수령인 정보 수정 (주소입력대기/완료 상태에서만)
  const openRecipientModal = useCallback((delivery: GiftDeliveryForAdmin) => {
    setRecipientModal({
      deliveryId: delivery._id,
      name: delivery.recipient?.name || '',
      phoneNumber: delivery.recipient?.phoneNumber || '',
      postalCode: delivery.recipient?.postalCode || '',
      address: delivery.recipient?.address || '',
      addressDetail: delivery.recipient?.addressDetail || '',
      isSaving: false,
    });
  }, []);

  const handleSaveRecipient = useCallback(async () => {
    if (!recipientModal) return;
    setRecipientModal((prev) => (prev ? { ...prev, isSaving: true } : null));
    try {
      await updateGiftDeliveryRecipient(
        recipientModal.deliveryId,
        {
          name: recipientModal.name || undefined,
          phoneNumber: recipientModal.phoneNumber || undefined,
          postalCode: recipientModal.postalCode || undefined,
          address: recipientModal.address || undefined,
          addressDetail: recipientModal.addressDetail || undefined,
          // 주소를 재검색한 경우에만 카카오 원문(권역 배송비 근거)이 함께 온다
          ...recipientRegionPatch(recipientModal.region),
        },
        ROLE,
      );
      await invalidateDashboard();
      setRecipientModal(null);
      alert('수령인 정보가 수정되었습니다.');
    } catch (error) {
      console.error('수령인 정보 수정 실패:', error);
      alert('수정에 실패했습니다.');
      setRecipientModal((prev) => (prev ? { ...prev, isSaving: false } : null));
    }
  }, [recipientModal, invalidateDashboard]);

  const openStatusModal = useCallback((delivery: GiftDeliveryForAdmin) => {
    const next = nextStatusMap[delivery.deliveryStatus];
    if (!next) return;
    setStatusModal({
      deliveryIds: [delivery._id],
      currentStatus: delivery.deliveryStatus,
      nextStatus: next,
      trackingNumber: '',
      carrier: '',
      shippedAt: toLocalDateTimeString(),
      deliveredAt: toLocalDateTimeString(),
      deliveredImageFile: null,
      deliveredImagePreview: null,
      isBulk: false,
      showRevert: false,
      revertConfirmText: '',
      existingCarrier: delivery.shipping?.carrier || '',
      existingTrackingNumber: delivery.shipping?.trackingNumber || '',
      existingShippedAt: delivery.shipping?.shippedAt
        ? toLocalDateTimeString(new Date(delivery.shipping.shippedAt))
        : '',
    });
  }, []);

  const openBulkStatusModal = useCallback(() => {
    const targets = rows.filter((d) => selectedIds.has(d._id));
    const first = targets[0];
    if (!first) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    if (targets.some((d) => d.deliveryStatus !== first.deliveryStatus)) {
      alert('서로 다른 상태의 항목이 섞여 있습니다. 같은 상태끼리 선택해주세요.');
      return;
    }
    const next = nextStatusMap[first.deliveryStatus];
    if (!next) {
      alert('상태를 변경할 수 없는 항목이 포함되어 있습니다.');
      return;
    }
    setStatusModal({
      deliveryIds: targets.map((d) => d._id),
      currentStatus: first.deliveryStatus,
      nextStatus: next,
      trackingNumber: '',
      carrier: '',
      shippedAt: toLocalDateTimeString(),
      deliveredAt: toLocalDateTimeString(),
      deliveredImageFile: null,
      deliveredImagePreview: null,
      isBulk: true,
      showRevert: false,
      revertConfirmText: '',
      existingCarrier: '',
      existingTrackingNumber: '',
      existingShippedAt: '',
    });
  }, [selectedIds, rows]);

  const handleConfirmStatusChange = useCallback(async () => {
    if (!statusModal) return;

    const shipping: UpdateDeliveryStatusDto['shipping'] =
      statusModal.nextStatus === GiftDeliveryStatus.Shipping
        ? {
            trackingNumber: statusModal.trackingNumber || undefined,
            carrier: statusModal.carrier || undefined,
            shippedAt: statusModal.shippedAt
              ? new Date(statusModal.shippedAt).toISOString()
              : new Date().toISOString(),
          }
        : statusModal.nextStatus === GiftDeliveryStatus.Delivered
        ? {
            deliveredAt: statusModal.deliveredAt
              ? new Date(statusModal.deliveredAt).toISOString()
              : new Date().toISOString(),
          }
        : undefined;

    if (
      statusModal.nextStatus === GiftDeliveryStatus.Delivered &&
      statusModal.deliveredImageFile
    ) {
      try {
        const img = await postImage(statusModal.deliveredImageFile, assetRole());
        if (shipping) {
          shipping.deliveredImage = img._id;
        }
      } catch {
        alert('이미지 업로드에 실패했습니다.');
        return;
      }
    }

    if (statusModal.isBulk) {
      setIsBulkProcessing(true);
      setStatusModal(null);
      let successCount = 0;
      let failCount = 0;

      for (const id of statusModal.deliveryIds) {
        try {
          await updateGiftDeliveryStatus(id, statusModal.nextStatus, shipping, ROLE);
          successCount++;
        } catch (error) {
          console.error(`상태 변경 실패 (${id}):`, error);
          failCount++;
        }
      }

      setIsBulkProcessing(false);
      setSelectedIds(new Set());
      await invalidateDashboard();

      if (failCount === 0) {
        alert(`${successCount}건 상태 변경 완료`);
      } else {
        alert(`성공: ${successCount}건, 실패: ${failCount}건`);
      }
    } else {
      const id = statusModal.deliveryIds[0] ?? '';
      setProcessingId(id);
      setStatusModal(null);

      try {
        await updateGiftDeliveryStatus(id, statusModal.nextStatus, shipping, ROLE);
        await invalidateDashboard();
        alert('상태 변경이 완료되었습니다.');
      } catch (error) {
        console.error('상태 변경 실패:', error);
        alert('상태 변경에 실패했습니다.');
      } finally {
        setProcessingId(null);
      }
    }
  }, [statusModal, assetRole, invalidateDashboard]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const isAllSelected = useMemo(
    () => rows.length > 0 && rows.every((d) => selectedIds.has(d._id)),
    [rows, selectedIds],
  );

  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((d) => d._id)));
    }
  }, [isAllSelected, rows]);

  // 선택한 행들을 엑셀(xls)로 저장
  const exportSelectedToExcel = useCallback(() => {
    const targets = rows.filter((d) => selectedIds.has(d._id));
    if (targets.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    const esc = (v: string | undefined) =>
      (v || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const dateStr = new Date().toISOString().slice(0, 10);
    const s = (v: string) =>
      `<Cell ss:StyleID="d"><Data ss:Type="String">${esc(v)}</Data></Cell>`;
    const n = (v: number) =>
      `<Cell ss:StyleID="d"><Data ss:Type="Number">${v}</Data></Cell>`;
    const h = (v: string) =>
      `<Cell ss:StyleID="h"><Data ss:Type="String">${esc(v)}</Data></Cell>`;
    const f = (v: string) =>
      `<Cell ss:StyleID="f"><Data ss:Type="String">${esc(v)}</Data></Cell>`;
    const fn = (v: number) =>
      `<Cell ss:StyleID="f"><Data ss:Type="Number">${v}</Data></Cell>`;

    let totalQty = 0;
    const dataRows = targets.map((d, idx) => {
      const o = getOrdererInfo(d);
      const itm =
        typeof d.giftItem === 'object' ? (d.giftItem as GiftItemForStore) : null;
      totalQty += d.quantity || 0;
      return (
        '<Row>' +
        n(idx + 1) +
        s(d.fullCode || d.code || '') +
        s(getItemName(d)) +
        s(itm?.giftItemInfo?.productCode || '') +
        n(d.quantity || 0) +
        s(typeLabel[d.type] || d.type) +
        s(statusLabel[d.deliveryStatus]?.text || d.deliveryStatus) +
        s(d.recipient?.name || '') +
        s(d.recipient?.phoneNumber || '') +
        s(d.recipient?.postalCode || '') +
        s(d.recipient?.address || '') +
        s(d.recipient?.addressDetail || '') +
        s(
          d.recipient?.submittedAt
            ? new Date(d.recipient.submittedAt).toLocaleString('ko-KR')
            : '',
        ) +
        s(o.name) +
        s(o.phone) +
        '</Row>'
      );
    });

    const bdr =
      'Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/></Borders';
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Styles>
 <Style ss:ID="Default"><Font ss:FontName="맑은 고딕" ss:Size="9"/></Style>
 <Style ss:ID="t"><Font ss:FontName="맑은 고딕" ss:Size="12" ss:Bold="1"/></Style>
 <Style ss:ID="sub"><Font ss:FontName="맑은 고딕" ss:Size="9" ss:Color="#666666"/></Style>
 <Style ss:ID="g"><Font ss:FontName="맑은 고딕" ss:Size="9" ss:Bold="1"/><Interior ss:Color="#D9D9D9" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/><${bdr}></Style>
 <Style ss:ID="h"><Font ss:FontName="맑은 고딕" ss:Size="9" ss:Bold="1"/><Interior ss:Color="#F2F2F2" ss:Pattern="Solid"/><Alignment ss:Vertical="Center"/><${bdr}></Style>
 <Style ss:ID="d"><Font ss:FontName="맑은 고딕" ss:Size="9"/><Alignment ss:Vertical="Center" ss:WrapText="1"/><${bdr}></Style>
 <Style ss:ID="f"><Font ss:FontName="맑은 고딕" ss:Size="9" ss:Bold="1"/><Interior ss:Color="#F2F2F2" ss:Pattern="Solid"/><Alignment ss:Vertical="Center"/><Borders><Border ss:Position="Top" ss:LineStyle="Double" ss:Weight="3" ss:Color="#333333"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#999999"/></Borders></Style>
</Styles>
<Worksheet ss:Name="배송목록">
<Table>
 <Column ss:Width="35"/><Column ss:Width="100"/><Column ss:Width="140"/><Column ss:Width="80"/>
 <Column ss:Width="45"/><Column ss:Width="60"/><Column ss:Width="70"/><Column ss:Width="70"/>
 <Column ss:Width="100"/><Column ss:Width="55"/><Column ss:Width="180"/><Column ss:Width="100"/>
 <Column ss:Width="115"/><Column ss:Width="70"/><Column ss:Width="100"/>
 <Row ss:Height="24"><Cell ss:StyleID="t"><Data ss:Type="String">배송 목록</Data></Cell></Row>
 <Row ss:Height="16"><Cell ss:StyleID="sub"><Data ss:Type="String">출력일: ${dateStr}   총 ${
      targets.length
    }건   총 수량: ${totalQty}개</Data></Cell></Row>
 <Row ss:Height="4"/>
 <Row ss:Height="20">
  <Cell ss:StyleID="g"><Data ss:Type="String"></Data></Cell>
  <Cell ss:StyleID="g"><Data ss:Type="String"></Data></Cell>
  <Cell ss:StyleID="g" ss:MergeAcross="4"><Data ss:Type="String">상품 정보</Data></Cell>
  <Cell ss:StyleID="g" ss:MergeAcross="5"><Data ss:Type="String">수령 · 배송 정보</Data></Cell>
  <Cell ss:StyleID="g" ss:MergeAcross="1"><Data ss:Type="String">보내는분</Data></Cell>
 </Row>
 <Row ss:Height="18">
  ${h('No')}${h('코드')}${h('상품명')}${h('상품코드')}${h('수량')}${h('타입')}${h('상태')}
  ${h('수령인')}${h('연락처')}${h('우편번호')}${h('주소')}${h('상세주소')}${h('입력일시')}
  ${h('성함')}${h('연락처')}
 </Row>
 ${dataRows.join('\n ')}
 <Row ss:Height="22">
  ${f('')}${f('')}${f('합계 (' + targets.length + '건)')}${f('')}
  ${fn(totalQty)}
  ${f('')}${f('')}${f('')}${f('')}${f('')}${f('')}${f('')}${f('')}${f('')}${f('')}
 </Row>
</Table>
</Worksheet>
</Workbook>`;
    const blob = new Blob([xml], {
      type: 'application/vnd.ms-excel;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `배송목록_${dateStr}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, selectedIds]);

  if (dashboardStatus === 'loading' || dashboardStatus === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">배송 관리</h1>
        <p className="mt-1 text-sm text-gray-500">
          신규 주문을 발주 → 배송 → 완료 단계로 처리하세요.
        </p>
      </div>

      {/* 상단 통계 카드 (클릭 시 해당 상태 묶음으로 필터 전환) */}
      <div className="grid grid-cols-3 gap-4 pb-4">
        {[
          {
            key: 'need',
            label: '배송관리 필요',
            value: statistics
              ? statistics.waitingAddress +
                statistics.addressSubmitted +
                statistics.preparing +
                statistics.shipping
              : undefined,
            statuses: DEFAULT_STATUSES,
            bg: 'bg-indigo-50',
            activeBg: 'ring-2 ring-indigo-400 bg-indigo-100',
            text: 'text-indigo-700',
            sub: 'text-indigo-600',
          },
          {
            key: 'delivered',
            label: '배송완료',
            value: statistics?.delivered,
            statuses: [GiftDeliveryStatus.Delivered],
            bg: 'bg-green-50',
            activeBg: 'ring-2 ring-green-400 bg-green-100',
            text: 'text-green-700',
            sub: 'text-green-600',
          },
          {
            key: 'all',
            label: '전체',
            value: statistics?.total,
            statuses: FILTERABLE_STATUSES,
            bg: 'bg-gray-100',
            activeBg: 'ring-2 ring-gray-400 bg-gray-200',
            text: 'text-gray-700',
            sub: 'text-gray-600',
          },
        ].map((card) => {
          const active =
            statusFilter.length === card.statuses.length &&
            card.statuses.every((s) => statusFilter.includes(s));
          return (
            <button
              key={card.key}
              onClick={() => setStatusFilter(card.statuses)}
              className={`rounded-lg p-4 text-center transition-all ${
                active ? card.activeBg : card.bg
              } cursor-pointer hover:opacity-80`}
            >
              <p className={`text-2xl font-bold ${card.text}`}>{card.value ?? '-'}</p>
              <p className={`text-sm ${card.sub}`}>{card.label}</p>
            </button>
          );
        })}
      </div>

      {/* 필터 */}
      <div className="mb-4 space-y-2">
        {/* 상태 다중 선택 칩 (대기/진행중 상태는 현재 건수를 함께 노출) */}
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERABLE_STATUSES.map((s) => {
            const active = statusFilter.includes(s);
            const count = COUNTABLE_STATUSES.includes(s) ? statusCounts[s] : undefined;
            const isNewChip = s === NEW_ORDER_STATUS;
            // 신규 주문은 건수가 있으면 눈에 띄게(로즈), 그 외엔 상태색에 맞춰 표시
            const countClass =
              isNewChip && count
                ? 'bg-rose-500 text-white'
                : active
                ? 'bg-white/25 text-white'
                : 'bg-gray-100 text-gray-500';
            return (
              <button
                key={s}
                onClick={() => toggleStatus(s)}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                  active
                    ? 'border-indigo-600 bg-indigo-600 text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {statusLabel[s]?.text ?? s}
                {count !== undefined && (
                  <span
                    className={`inline-flex min-w-[1.125rem] justify-center rounded-full px-1 text-[10px] font-bold leading-4 ${countClass}`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700"
          >
            <option value="all">전체 타입</option>
            <option value={GiftDeliveryType.Individual}>개별배송</option>
            <option value={GiftDeliveryType.Bulk}>일괄배송</option>
          </select>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="수령인 · 연락처 · 상품명 · 주소"
            className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm"
          />
          <button
            onClick={() => invalidateDashboard()}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            새로고침
          </button>
        </div>
      </div>

      {/* 신규 주문(발주 대기) 알림 배너 — 빠른 처리 유도 */}
      {newOrderCount > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="animate-pulse rounded bg-rose-500 px-2 py-0.5 text-xs font-bold text-white">
              NEW
            </span>
            <p className="text-sm font-semibold text-emerald-900">
              신규 주문 <b className="text-rose-600">{newOrderCount}건</b>이 발주를
              기다리고 있어요. 빠르게 처리해 주세요!
            </p>
          </div>
          {!(statusFilter.length === 1 && statusFilter[0] === NEW_ORDER_STATUS) && (
            <button
              onClick={() => setStatusFilter([NEW_ORDER_STATUS])}
              className="shrink-0 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              신규만 보기 →
            </button>
          )}
        </div>
      )}

      {/* 선택 항목 일괄 처리 바 */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg bg-indigo-50 px-4 py-3">
          <span className="text-sm font-medium text-indigo-700">
            {selectedIds.size}건 선택됨
          </span>
          <button
            onClick={openBulkStatusModal}
            disabled={isBulkProcessing}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isBulkProcessing ? '처리 중...' : '일괄 상태 변경'}
          </button>
          <button
            onClick={exportSelectedToExcel}
            className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
          >
            엑셀 저장 ({selectedIds.size}건)
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            선택 해제
          </button>
        </div>
      )}

      {/* 오늘 처리 내역 토글 (배송완료 칩 선택 시엔 아래 통합 목록으로 대체) */}
      {!showDelivered && todayDelivered.length > 0 && (
        <div className="pb-2">
          <button
            onClick={() => setShowTodayProcessed((prev) => !prev)}
            className="flex w-full items-center justify-between rounded-lg bg-green-50 px-4 py-3 text-left transition-colors hover:bg-green-100"
          >
            <span className="text-sm font-medium text-green-800">
              오늘 배송완료 ({todayDelivered.length}건)
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 text-green-600 transition-transform ${
                showTodayProcessed ? 'rotate-180' : ''
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showTodayProcessed && (
            <div className="mt-2">
              <DeliveredDeliveryTable
                deliveries={todayDelivered}
                onOpenImage={setImageModal}
                onOpenEdit={openEditModal}
              />
            </div>
          )}
        </div>
      )}

      {/* 배송완료 전체 내역 로딩 안내 */}
      {deliveredLoading && (
        <div className="mb-3 rounded-lg bg-green-50 px-4 py-2 text-sm text-green-700">
          배송완료 내역을 불러오는 중...
        </div>
      )}

      {/* Table */}
      {rows.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          {statusFilter.length === 0
            ? '조회할 상태를 선택해주세요.'
            : '해당하는 배송 건이 없습니다.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="break-keep bg-gray-50 text-[11px] font-medium uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleToggleAll}
                    disabled={rows.length === 0}
                    title="전체 선택"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                  />
                </th>
                <th className="px-3 py-2 text-left">순번</th>
                <th className="px-3 py-2 text-left">배송요청</th>
                <th className="px-3 py-2 text-left">상품</th>
                {showOptionColumn && <th className="px-3 py-2 text-left">상품옵션</th>}
                <th className="px-3 py-2 text-left">수량</th>
                <th className="px-3 py-2 text-left">수령인</th>
                <th className="px-3 py-2 text-left">상태</th>
                <th className="px-3 py-2 text-left">송장 / 배송완료</th>
                <th className="px-3 py-2 text-left">처리</th>
                <th className="px-3 py-2 text-left">코드</th>
                <th className="px-3 py-2 text-left">주문자</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d, idx) => {
                // 먼저 들어온(오래된) 주문이 1번. 목록은 최신순이므로 역순으로 번호 부여
                const seq = rows.length - idx;
                const orderer = getOrdererInfo(d);
                const badge = statusLabel[d.deliveryStatus] ?? {
                  text: d.deliveryStatus,
                  className: 'bg-gray-100 text-gray-600',
                };
                const canAdvance = !!nextStatusMap[d.deliveryStatus];
                const canRevert = !!prevStatusMap[d.deliveryStatus];
                // 발주 대기 신규 주문: 행 강조 + NEW 뱃지 + 발주 버튼 강조
                const isNew = d.deliveryStatus === NEW_ORDER_STATUS;
                const isSelected = selectedIds.has(d._id);
                // 주소입력대기/완료 상태에서만 수령인 정보 수정 가능
                const recipientEditable =
                  d.deliveryStatus === GiftDeliveryStatus.WaitingAddress ||
                  d.deliveryStatus === GiftDeliveryStatus.AddressSubmitted;
                const recipient = d.recipient;
                const fullAddress = recipient
                  ? `(${recipient.postalCode}) ${recipient.address} ${
                      recipient.addressDetail || ''
                    }`.trim()
                  : '';
                const requestedAt = formatRelativeDateTime(recipient?.submittedAt);
                const completedAt =
                  d.deliveryStatus === GiftDeliveryStatus.Delivered
                    ? formatRelativeDateTime(d.shipping?.deliveredAt)
                    : null;
                const itm =
                  typeof d.giftItem === 'object'
                    ? (d.giftItem as GiftItemForStore)
                    : null;
                const deliveredImg = d.shipping?.deliveredImage;
                const deliveredImgUrl =
                  deliveredImg && typeof deliveredImg === 'object'
                    ? fileBucketUrl(deliveredImg.url, 'w400')
                    : null;
                const rowBg = isSelected
                  ? 'bg-indigo-50 hover:bg-indigo-50'
                  : isNew
                  ? 'bg-emerald-50/70 hover:bg-emerald-50'
                  : 'hover:bg-gray-50';
                return (
                  <Fragment key={d._id}>
                    {/* 주문 간 구분선 (같은 주문의 두 행은 아래 점선으로만 구분) */}
                    <tr className={`border-t-2 border-gray-200 align-middle ${rowBg}`}>
                      {/* 0. 선택 체크박스 (일괄 상태변경 / 엑셀 저장 대상) */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(d._id)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      {/* 1. 순번 (신규 주문은 좌측 강조선) */}
                      <td
                        className={`whitespace-nowrap px-3 py-2.5 text-sm font-bold text-black ${
                          isNew ? 'border-l-4 border-emerald-500' : ''
                        }`}
                      >
                        {seq}
                      </td>
                      {/* 2. 배송요청 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        {requestedAt ? (
                          <div className="leading-tight">
                            <span
                              className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                requestedAt.relative === '오늘'
                                  ? 'bg-rose-100 text-rose-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {requestedAt.relative}
                            </span>
                            <p className="mt-0.5 text-sm font-bold text-gray-900">
                              {requestedAt.date}{' '}
                              <span className="text-xs font-medium text-gray-400">
                                {requestedAt.time}
                              </span>
                            </p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </td>
                      {/* 3. 상품 */}
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          {getImageUrl(d) && (
                            <img
                              src={getImageUrl(d)}
                              alt=""
                              className="h-8 w-8 flex-shrink-0 rounded object-cover"
                            />
                          )}
                          <div>
                            <p className="max-w-[220px] whitespace-normal break-words font-medium text-gray-800">
                              {getItemName(d)}
                            </p>
                            <div className="mt-0.5 flex items-center gap-1.5">
                              <span
                                className={`rounded-full px-1.5 py-px text-[10px] font-semibold ${
                                  typeBadgeStyle[d.type] || 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {typeLabel[d.type] || d.type}
                              </span>
                              {itm?.giftItemInfo?.productCode && (
                                <span className="font-mono text-[11px] text-gray-400">
                                  {itm.giftItemInfo.productCode}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* 4. 상품옵션 (선택한 추가옵션 — 출고 시 어떤 옵션 상품을 보낼지 확인) */}
                      {showOptionColumn && (
                        <td className="px-3 py-2.5">
                          {d.selectedOptions && d.selectedOptions.length > 0 ? (
                            <ul className="max-w-[240px] space-y-1">
                              {d.selectedOptions.map((opt, i) => (
                                <li
                                  key={`${opt.optionName}-${opt.slotIndex ?? i}`}
                                  className="flex flex-wrap items-baseline gap-1.5 text-sm leading-tight"
                                >
                                  <span className="text-gray-500">
                                    {opt.optionName}
                                    {/* selectionCount>1: 슬롯 번호를 1부터 표시 (비누종류 1, 2 …) */}
                                    {opt.slotIndex !== undefined
                                      ? ` ${opt.slotIndex + 1}`
                                      : ''}
                                  </span>
                                  <span className="font-semibold text-indigo-600">
                                    {opt.choiceName}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      {/* 5. 수량 */}
                      <td className="whitespace-nowrap px-3 py-2.5 text-gray-700">
                        {d.quantity}개
                      </td>
                      {/* 6. 수령인 (주소입력대기/완료 상태는 클릭하여 수정) */}
                      <td
                        className={`whitespace-nowrap px-3 py-2.5 ${
                          recipientEditable ? 'cursor-pointer hover:bg-indigo-50' : ''
                        }`}
                        onClick={
                          recipientEditable ? () => openRecipientModal(d) : undefined
                        }
                        title={
                          recipientEditable ? '클릭하여 수령인 정보 수정' : undefined
                        }
                      >
                        <p className="font-medium text-gray-800">
                          {d.recipient?.name || '-'}
                          {recipientEditable && (
                            <span className="ml-1 text-[10px] text-indigo-400">✎</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatPhone(d.recipient?.phoneNumber)}
                        </p>
                      </td>
                      {/* 7. 상태 (신규 주문은 NEW 뱃지로 강조) */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <div className="flex items-center gap-1">
                          {isNew && (
                            <span className="animate-pulse rounded bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                              NEW
                            </span>
                          )}
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.className}`}
                          >
                            {badge.text}
                          </span>
                        </div>
                        {isNew && (
                          <p className="mt-1 text-[10px] font-semibold text-emerald-600">
                            발주 대기중 · 바로 처리
                          </p>
                        )}
                      </td>
                      {/* 8. 송장 / 배송완료 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        {/* 배송완료 건: 완료일 강조 (날짜 + 시각). 클릭 시 완료시각/사진 수정 */}
                        {completedAt && (
                          <button
                            onClick={() => openEditModal(d)}
                            title="배송완료 정보 수정 (완료시각/사진)"
                            className="mb-1 block text-left text-sm font-bold leading-tight text-gray-900 underline decoration-dotted underline-offset-2 hover:text-indigo-600"
                          >
                            {completedAt.date}{' '}
                            <span className="text-xs font-medium text-gray-400">
                              {completedAt.time}
                            </span>
                          </button>
                        )}
                        {/* 송장/발송 정보 (배송중·배송완료) */}
                        {(d.deliveryStatus === GiftDeliveryStatus.Shipping ||
                          d.deliveryStatus === GiftDeliveryStatus.Delivered) && (
                          <div className="space-y-0.5 text-[11px] leading-tight text-gray-400">
                            {d.shipping?.carrier && (
                              <p className="text-gray-500">{d.shipping.carrier}</p>
                            )}
                            {d.shipping?.trackingNumber ? (
                              d.deliveryStatus === GiftDeliveryStatus.Shipping ? (
                                <button
                                  onClick={() => openStatusModal(d)}
                                  title="송장 정보 수정"
                                  className="block font-mono text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-700"
                                >
                                  {d.shipping.trackingNumber}
                                </button>
                              ) : (
                                <p className="font-mono text-gray-500">
                                  {d.shipping.trackingNumber}
                                </p>
                              )
                            ) : d.deliveryStatus === GiftDeliveryStatus.Shipping ? (
                              <button
                                onClick={() => openStatusModal(d)}
                                className="font-semibold text-amber-600 underline decoration-dotted underline-offset-2 hover:text-amber-700"
                              >
                                송장 미입력 (입력)
                              </button>
                            ) : null}
                            {formatShortDateTime(d.shipping?.shippedAt) && (
                              <p>발송 {formatShortDateTime(d.shipping?.shippedAt)}</p>
                            )}
                            {deliveredImgUrl && (
                              <button
                                onClick={() =>
                                  setImageModal({
                                    url: deliveredImgUrl,
                                    itemName: getItemName(d),
                                    address: d.recipient?.address || '-',
                                    deliveredAt:
                                      formatShortDateTime(d.shipping?.deliveredAt) || '-',
                                  })
                                }
                                className="text-blue-500 underline decoration-dotted underline-offset-2 hover:text-blue-700"
                              >
                                완료사진 보기
                              </button>
                            )}
                          </div>
                        )}
                        {/* 그 외 상태: 표시할 배송정보 없음 */}
                        {d.deliveryStatus !== GiftDeliveryStatus.Shipping &&
                          d.deliveryStatus !== GiftDeliveryStatus.Delivered && (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                      </td>
                      {/* 9. 처리 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {canAdvance ? (
                            <button
                              onClick={() => openStatusModal(d)}
                              disabled={processingId === d._id}
                              className={`rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                                isNew
                                  ? 'bg-emerald-600 shadow-sm hover:bg-emerald-700'
                                  : 'bg-indigo-600 hover:bg-indigo-700'
                              }`}
                            >
                              {processingId === d._id
                                ? '처리중...'
                                : isNew
                                ? '발주 처리 →'
                                : nextStatusLabel[d.deliveryStatus] || '상태 변경'}
                            </button>
                          ) : (
                            !canRevert && (
                              <span className="text-xs text-gray-300">대기</span>
                            )
                          )}
                          {canRevert && (
                            <button
                              onClick={() => handleRevertStatus(d._id, d.deliveryStatus)}
                              disabled={revertingId === d._id}
                              title={prevStatusLabel[d.deliveryStatus]}
                              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            >
                              {revertingId === d._id ? '처리중...' : '되돌리기'}
                            </button>
                          )}
                        </div>
                      </td>
                      {/* 10. 코드 (클릭하여 _id 복사) */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <button
                          onClick={() => copyToClipboard(d._id)}
                          className="font-mono text-xs text-gray-400 hover:text-indigo-600"
                          title="클릭하여 _id 복사"
                        >
                          {d.fullCode || d.code || '-'}
                        </button>
                      </td>
                      {/* 11. 주문자 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <p className="text-gray-800">{orderer.name}</p>
                        <p className="text-xs text-gray-400">
                          {formatPhone(orderer.phone)}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          구매 {formatDate(orderer.purchasedAt)}
                        </p>
                      </td>
                    </tr>
                    {/* 배송지: 같은 주문의 둘째 행. 신규 주문은 윗행과 동일 색으로 한 카드처럼 묶는다 */}
                    <tr className={rowBg}>
                      <td
                        colSpan={showOptionColumn ? 12 : 11}
                        className={`px-3 py-2.5 align-middle ${
                          isNew
                            ? 'border-l-4 border-t border-dashed border-emerald-500 border-t-emerald-300'
                            : 'border-t border-dashed border-gray-300'
                        }`}
                      >
                        {recipient?.address ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <DesiredDeliveryDateBadge date={d.desiredDeliveryDate} />
                            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                              배송지
                            </span>
                            <p
                              className={`min-w-0 text-xs leading-snug text-gray-600 ${
                                recipientEditable
                                  ? 'cursor-pointer hover:text-indigo-600'
                                  : ''
                              }`}
                              onClick={
                                recipientEditable
                                  ? () => openRecipientModal(d)
                                  : undefined
                              }
                              title={
                                recipientEditable ? '클릭하여 배송지 수정' : undefined
                              }
                            >
                              ({recipient.postalCode}) {recipient.address}
                              {recipient.addressDetail
                                ? ` ${recipient.addressDetail}`
                                : ''}
                              {recipientEditable && (
                                <span className="ml-1 text-[10px] text-indigo-400">
                                  ✎
                                </span>
                              )}
                            </p>
                            <button
                              onClick={() => copyAddress(d._id, fullAddress)}
                              title="주소 복사"
                              className={`shrink-0 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
                                copiedAddressId === d._id
                                  ? 'border-green-300 bg-green-50 text-green-600'
                                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {copiedAddressId === d._id ? '✓ 복사됨' : '주소 복사'}
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                              배송지
                            </span>
                            {recipientEditable ? (
                              <button
                                onClick={() => openRecipientModal(d)}
                                className="text-xs text-indigo-500 underline decoration-dotted underline-offset-2 hover:text-indigo-700"
                              >
                                미입력 (클릭하여 입력)
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">미입력</span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {statusModal && (
        <StatusChangeModal
          modal={statusModal}
          setModal={setStatusModal}
          revertingId={revertingId}
          onConfirm={handleConfirmStatusChange}
          onRevert={handleRevertStatus}
          onSaveShipping={async () => {
            try {
              const targetId = statusModal.deliveryIds[0];
              if (!targetId) return;
              await updateGiftDeliveryStatus(
                targetId,
                GiftDeliveryStatus.Shipping,
                {
                  carrier: statusModal.existingCarrier.trim(),
                  trackingNumber: statusModal.existingTrackingNumber.trim(),
                  shippedAt: statusModal.existingShippedAt
                    ? new Date(statusModal.existingShippedAt).toISOString()
                    : undefined,
                },
                ROLE,
              );
              await invalidateDashboard();
              setStatusModal(null);
              alert('송장 정보가 수정되었습니다.');
            } catch {
              alert('송장 수정에 실패했습니다.');
            }
          }}
        />
      )}

      {imageModal && (
        <ImagePreviewModal modal={imageModal} onClose={() => setImageModal(null)} />
      )}

      {editModal && (
        <DeliveredEditModal
          modal={editModal}
          setModal={setEditModal}
          onSave={handleSaveEdit}
          onRevert={handleRevertDelivered}
        />
      )}

      {recipientModal && (
        <RecipientEditModal
          modal={recipientModal}
          setModal={setRecipientModal}
          onSave={handleSaveRecipient}
        />
      )}

      <Toast message={toastMessage} durationMs={toastDurationMs} />
    </div>
  );
};

export const AgencyAdminGiftDeliveryManagementPage: FC = () => (
  <div className="flex">
    <div className="flex flex-col">
      <StatPageSideBar />
    </div>
    <div className="mx-auto max-w-screen-2xl flex-1">
      <div className="py-8 px-10">
        <AgencyAdminGiftDeliveryContent />
      </div>
    </div>
  </div>
);
